import posthog from "posthog-js";

/**
 * PostHog analytics wrapper. Kept deliberately separate from Sentry: Sentry owns
 * errors/crash replay, PostHog owns product behaviour (autocapture, pageviews) and
 * session recordings for paid ("power") users.
 *
 * Init is imperative and guarded so importing these helpers into a component under
 * test never spins up PostHog or fires network calls (MSW runs with
 * `onUnhandledRequest: "error"`).
 */

let enabled = false;

const DEFAULT_HOST = "https://us.i.posthog.com";

type PersonProperties = {
  email?: string;
  plan: string | null;
  is_admin: boolean;
  billing_exempt: boolean;
  org_id: string | null;
};

/**
 * Initialize PostHog. No-op unless a project key is configured and we're running in
 * production. Unlike Sentry (which also runs in staging to catch pre-prod errors),
 * behaviour analytics is production-only so staging traffic never pollutes the
 * product data — and dev/tests never send.
 */
export function initAnalytics(): void {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const isProd = import.meta.env.VITE_ENVIRONMENT === "production";

  if (!(key && isProd)) return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || DEFAULT_HOST,
    // api_host is our managed reverse proxy in production; toolbar and other
    // links back into PostHog must still point at the app itself.
    ui_host: "https://us.posthog.com",
    autocapture: true,
    // A SPA: `true` captures only the initial load, missing client-side routes
    // such as the redirect to /sign-in and the link through to /sign-up.
    capture_pageview: "history_change",
    // Recording is off by default; we start it explicitly for paid users only.
    disable_session_recording: true,
    // Mirror the Sentry replay config — mask nothing. Seeing the design prompts and
    // generated images is the point of watching power-user sessions.
    session_recording: {
      maskAllInputs: false,
    },
  });

  enabled = true;
}

/** Associate the current session with a signed-in user and their person properties. */
export function identifyUser(id: string, properties: PersonProperties): void {
  if (!enabled) return;
  posthog.identify(id, properties);
}

/** Clear identity on sign-out so the next user isn't merged into this one. */
export function resetAnalytics(): void {
  if (!enabled) return;
  posthog.reset();
}

/** Start recording the current session. Called for paid users only. */
export function startRecording(): void {
  if (!enabled) return;
  posthog.startSessionRecording();
}

type TrackProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

/** Fire a product event. No-op unless analytics is enabled (production). */
export function trackEvent(name: string, properties?: TrackProperties): void {
  if (!enabled) return;
  posthog.capture(name, properties);
}
